import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { ReactElement } from 'react'
import type {
  LocationPermission,
  SuggesterSectionKey,
  SuggesterSelectSource,
  SuggesterSlot,
  SuggesterStation,
} from './types'
import {
  createSuggesterStore,
  applyCachedStatuses,
  disruptionSettleEvent,
  DISRUPTION_LOADING_CAP_MS,
} from './store'
import { buildSections } from './sections'
import { DisruptionDetails } from './DisruptionDetails'
import type { DisruptionLine } from './DisruptionDetails'
import { loadRecents, pushRecent, saveRecents } from './recents'
import { defaultStrings } from './strings'
import type { SuggesterStrings } from './strings'
import type { SearchService } from './searchService'
import type { FavoritesService } from './favorites'
import { SearchField } from './SearchField'
import { SuggestionSection } from './SuggestionSection'
import { LocationBanner } from './LocationBanner'
import { SkeletonRow } from './SkeletonRow'
import { EmptyState } from './EmptyState'
import { OfflineNotice } from './OfflineNotice'
import { SuggesterAlert } from './SuggesterAlert'
import styles from './Suggester.module.css'

/** Default search debounce — STRICT 350 ms cross-platform constant (spec §13). */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Network-state debounce — how long the browser must stay continuously
 * `online` before the `offline` Suggester state auto-recovers (spec §15).
 *
 * The contract leaves the exact interval as a TODO; 1500 ms is the
 * agreed cross-platform constant — long enough to ride out brief
 * connectivity blips so the `offline ⇄ online` UI never flickers, short
 * enough that a genuine reconnection re-runs the query promptly. The
 * Android `ConnectivityRestored` debounce uses the same value.
 */
const NETWORK_RECOVERY_DEBOUNCE_MS = 1500

/** Skeleton rows shown in the `loading` state — matches the Figma frame. */
const SKELETON_ROW_COUNT = 7

/**
 * Analytics event names — a cross-platform contract (spec §7). Identical
 * strings on iOS / Android / Web.
 */
const EVENT_OPEN = 'suggester_open'
const EVENT_SEARCH = 'suggester_search'
const EVENT_SELECT = 'suggester_select'
/** Disruption Details opened (spec §5 / §A1). */
const EVENT_DISRUPTION_OPEN = 'suggester_disruption_open'
// G-10 disruption-detail events (`disruption_detail_loaded` / `_error`) are
// emitted on the terminal settle via the pure `disruptionSettleEvent` mapper
// in `./store` (names + web enum values live there). `line_status_viewed` —
// the third G-10 event — is emitted by the HOST from the station-card
// `StatusBanner` surface (`src/analytics/disruptionEvents.ts`), not here:
// web's suggester rows show a per-STATION disruption composite already
// covered by `suggester_disruption_open`.

/**
 * Presentation chrome for {@link Suggester} (TASK-412).
 *
 * - `overlay` (default) — the phone full-screen modal: `position: fixed;
 *   inset: 0`, opaque background, the only layout the package shipped
 *   before TASK-412. Every existing call site keeps this by omission.
 * - `sidePanel` — the tablet/desktop floating card: a fixed column
 *   mirroring the host `.expandedSidebar` (Liquid-Glass), into which the
 *   suggester paints its search field + section list. The search field +
 *   close button + Escape are KEPT — only the wrapper chrome differs.
 *   Mirrors the iOS `SuggesterPresentation.sidePanel` (drops the
 *   grabber, content stays the same).
 */
export type SuggesterLayout = 'overlay' | 'sidePanel'

/**
 * Result of a host nearby-resolution attempt — the geolocation outcome
 * the Suggester needs to drive the §14 Location flow.
 */
export interface NearbyResolution {
  /** The permission state observed during the attempt. */
  readonly permission: LocationPermission
  /**
   * Nearest stations (distance-sorted or not — the store sorts), each
   * carrying `distanceMeters`. Empty / omitted when no fix was obtained.
   */
  readonly stations?: readonly SuggesterStation[]
}

/**
 * Props for {@link Suggester}.
 *
 * The package is domain-agnostic — the host adapts its schema into
 * {@link SuggesterStation}s, wires the analytics sink, supplies the
 * search + favourites services, and (optionally) a nearby resolver. The
 * Suggester never touches the route store, schema, geolocation, or an
 * analytics SDK directly.
 */
export interface SuggesterProps {
  /** Which route slot is being filled — echoed back on selection. */
  readonly slot: SuggesterSlot
  /**
   * Full station catalogue, host-adapted from the metro schema. Used to
   * resolve recent / favourite ids into stations for the idle sections.
   */
  readonly catalogue: readonly SuggesterStation[]
  /**
   * Backend-provided Popular fallback list (8 stations) shown in `idle`
   * when Nearby + Favorites + Recents are all empty (spec §3.4).
   */
  readonly popular: readonly SuggesterStation[]
  /** Search service — online backend with bundled offline fallback (§13). */
  readonly searchService: SearchService
  /** Shared favourites store — read for the idle Favorites section (§16). */
  readonly favoritesService: FavoritesService
  /**
   * Emitted on a `SuggestionRow` tap (any section / state). The caller
   * writes the station into the slot then dismisses the Suggester
   * (spec §5 — the Suggester never mutates the route slot directly).
   */
  readonly onStationSelected: (station: SuggesterStation, slot: SuggesterSlot) => void
  /** Called when the user dismisses the overlay (close control / Escape). */
  readonly onClose: () => void
  /**
   * Analytics sink. Receives the spec §7 events with their exact
   * parameter shapes. The host forwards these to `MammothAnalytics`.
   */
  readonly onAnalytics: (event: string, params: Record<string, unknown>) => void
  /**
   * Host geolocation bridge — drives the §14 Location flow. Invoked when
   * the location banner is tapped; the host requests the browser
   * permission, obtains a fix, resolves the nearest stations and returns
   * a {@link NearbyResolution}. The Suggester maps the outcome onto the
   * `nearby` state or the permission / denied alerts.
   */
  readonly resolveNearby: () => Promise<NearbyResolution>
  /**
   * Host permission probe (#38, spec §14) — reads the browser's current
   * geolocation permission **without** prompting (e.g. via
   * `navigator.permissions.query({ name: 'geolocation' })`). Run once at
   * mount: on `granted` the reducer hides the "use my location" banner
   * and the Suggester auto-resolves Nearby (the user lands directly on
   * the Nearby section, no tap); on `prompt` / `denied` the banner stays.
   * Optional — when omitted the banner starts visible and only the
   * banner tap drives the flow (back-compat / SSR-safe). Mirrors the iOS
   * `.task` mount probe.
   */
  readonly probePermission?: () => Promise<LocationPermission>
  /**
   * Live-status cache (spec §8), naptanId-keyed, supplied by the host's
   * status preloader / refresh loop. Projected onto every rendered row
   * by {@link applyCachedStatuses} so a row painted from a status-less
   * payload (Recents / Favorites / Nearby / Popular) flips to its
   * Closed / Advisory badge in place once the cache lands a disruption.
   * Optional / `{}` when the host has no status cache wired (bundled-only
   * runtime). An inline `status` on a station always wins (fresher).
   */
  readonly cachedStatuses?: Readonly<Record<string, SuggesterStation['status']>>
  /**
   * Schema line map (`lineId` → {@link DisruptionLine}) — resolves the
   * Disruption Details affected-lines chips (spec §5, R4). Chips whose
   * `lineId` is absent here are dropped silently. Optional / `{}` when
   * the host has no line metadata wired.
   */
  readonly lines?: Readonly<Record<string, DisruptionLine>>
  /**
   * Selection recorder (spec §13) — fired fire-and-forget on every
   * station selection so the backend can rank Popular by real usage
   * (`POST /stations/selection { naptanId, slot }`). Skipped for
   * off-schema stations with no `naptanId`. Optional / bundled-only
   * runtime omits it. Failures inside the recorder never block selection.
   */
  readonly onRecordSelection?: (selection: { naptanId: string; slot: SuggesterSlot }) => void
  /**
   * Called when the user picks "Open Settings" in the denied alert. On
   * Web there is no OS settings deep-link — the host shows copy guidance
   * (spec §14). Optional; when omitted the action just dismisses.
   */
  readonly onOpenSettings?: () => void
  /** Localised string set. @defaultValue {@link defaultStrings} */
  readonly strings?: SuggesterStrings
  /** Debounce window override (ms). @defaultValue {@link SEARCH_DEBOUNCE_MS} */
  readonly debounceMs?: number
  /**
   * Network-recovery debounce override (ms) — how long the browser must
   * stay continuously `online` before the `offline` state auto-recovers
   * (spec §15). @defaultValue {@link NETWORK_RECOVERY_DEBOUNCE_MS}
   */
  readonly networkRecoveryMs?: number
  /**
   * Presentation chrome (TASK-412). `overlay` is the phone full-screen
   * modal; `sidePanel` is the tablet/desktop floating card mounted by the
   * host inside its `.expandedSidebar`. @defaultValue `'overlay'`
   */
  readonly layout?: SuggesterLayout
  /**
   * Controlled search query (TASK-412 pills-as-search). Only honoured in
   * `layout='sidePanel'`, where the suggester renders NO SearchField and
   * NO close button — the host's RouteCard active pill IS the query
   * input. The host owns the string and pushes it here; the suggester
   * drives its 6 phases off it (same 350 ms debounce). In `overlay`
   * layout the query is owned internally (the built-in SearchField) and
   * this prop is ignored.
   */
  readonly query?: string
}

/**
 * Suggester — mobile full-screen modal overlay for picking a route
 * From / To station (spec §1, §2 — phone layout only).
 *
 * Owns a private {@link createSuggesterStore} and renders the six §4
 * states (`idle` / `loading` / `results` / `empty` / `offline` /
 * `nearby`) plus the app-drawn "Location Access Required" alert. The
 * system permission *prompt* is the browser's own native dialog (shown
 * by `navigator.geolocation`) — the Suggester renders no in-app UI for
 * it. `layout="sidePanel"` (TASK-412) swaps the overlay chrome for the
 * tablet/desktop floating-card chrome; the contents are unchanged.
 */
export function Suggester({
  slot,
  catalogue,
  popular,
  searchService,
  favoritesService,
  onStationSelected,
  onClose,
  onAnalytics,
  resolveNearby,
  probePermission,
  cachedStatuses,
  lines,
  onRecordSelection,
  onOpenSettings,
  strings = defaultStrings,
  debounceMs = SEARCH_DEBOUNCE_MS,
  networkRecoveryMs = NETWORK_RECOVERY_DEBOUNCE_MS,
  layout = 'overlay',
  query: controlledQuery,
}: SuggesterProps): ReactElement {
  // sidePanel (TASK-412 pills-as-search): the host's RouteCard active
  // pill is the query input, so the suggester renders NO header (no
  // SearchField, no close button) and the query is CONTROLLED by the
  // host. overlay keeps the built-in SearchField + internal query.
  const isSidePanel = layout === 'sidePanel'
  // One store per mounted Suggester session, seeded with recents + favorites.
  //
  // Held in `useState` rather than a lazily-filled `useRef`: the store is read
  // during render (`useSyncExternalStore` below, plus every effect dep array
  // that closes over it), and reading a ref during render is exactly what
  // `react-hooks/refs` forbids — a ref read can silently miss a re-render.
  // `useState` with an initializer function gives the same "created once per
  // mount, never re-created on re-render" guarantee for a value that IS a
  // legitimate render input.
  //
  // Under dev StrictMode React invokes the initializer twice and discards one
  // result. That is harmless here and deliberately accepted:
  // `createSuggesterStore` is pure (a closure over a local state variable and a
  // listener Set — no registration, no I/O), and the two seed dispatches only
  // READ (`loadRecents`, `favoritesService.getAll`). The discarded store is
  // garbage. Do NOT "optimize" this back into a ref.
  const [store] = useState(() => {
    const created = createSuggesterStore()
    created.dispatch({ kind: 'RecentsLoaded', recentIds: loadRecents() })
    created.dispatch({ kind: 'FavoritesChanged', favoriteIds: favoritesService.getAll() })
    return created
  })

  const state = useSyncExternalStore(store.subscribe, store.getState)
  const [resolvingLocation, setResolvingLocation] = useState(false)

  // suggester_open — fire exactly once on mount. The StrictMode-safe
  // latch guards the dev-mode double effect invocation.
  const openReportedRef = useRef(false)
  useEffect(() => {
    if (openReportedRef.current) return
    openReportedRef.current = true
    onAnalytics(EVENT_OPEN, { slot })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Live favourites — re-sync the store whenever the shared service
  // changes (e.g. StationDetails toggles a favourite in another view).
  useEffect(() => {
    return favoritesService.subscribe(() => {
      store.dispatch({ kind: 'FavoritesChanged', favoriteIds: favoritesService.getAll() })
    })
  }, [favoritesService, store])

  // Escape — when Disruption Details is open it pops Details first (the
  // web equivalent of the iOS NavigationStack back); otherwise it closes
  // the overlay (mirrors the host StationDetailsModal).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      if (store.getState().disruption !== null) {
        store.dispatch({ kind: 'DisruptionDismissed' })
        return
      }
      onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, store])

  // Debounced search through the SearchService. Keyed off `searchNonce`
  // (bumped by every `QueryChanged` AND by `ConnectivityRestored`) so a
  // post-reconnection re-run of the SAME query still fires. Each run
  // schedules a single timer; the previous one is cancelled so only the
  // settled query runs — and suggester_search fires once per settled
  // query (spec §7). The async result is dropped if the effect has been
  // re-run before it resolves.
  useEffect(() => {
    const trimmed = state.query.trim()
    if (trimmed.length === 0) return
    let cancelled = false
    const handle = window.setTimeout(() => {
      onAnalytics(EVENT_SEARCH, { query_length: trimmed.length })
      void searchService.search(state.query).then((result) => {
        if (cancelled) return
        store.dispatch({
          kind: 'SearchSettled',
          stations: result.stations,
          offline: result.offline,
        })
      })
    }, debounceMs)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
    // `state.query` is intentionally NOT a dep — `searchNonce` is the
    // single source of "run a search now"; the effect reads the live
    // query at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchNonce, searchService, store, onAnalytics, debounceMs])

  // §15 offline auto-recovery — connectivity listener. When the browser
  // reports `online` while the Suggester sits in `offline`, wait out a
  // network-state debounce (so a brief blip does not flicker the UI)
  // and then dispatch `ConnectivityRestored`, which re-runs the current
  // query against the backend. A subsequent `offline` event before the
  // debounce elapses cancels the pending recovery. Parity with the
  // Android `ConnectivityRestored` flow.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const clearPending = (): void => {
      if (timer !== undefined) {
        clearTimeout(timer)
        timer = undefined
      }
    }
    const onOnline = (): void => {
      clearPending()
      timer = setTimeout(() => {
        timer = undefined
        // Re-check `navigator.onLine` at fire time: if connectivity
        // dropped again during the debounce window, do not recover.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return
        store.dispatch({ kind: 'ConnectivityRestored' })
      }, networkRecoveryMs)
    }
    // An `offline` event mid-debounce cancels the queued recovery.
    const onOffline = (): void => clearPending()
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      clearPending()
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [store, networkRecoveryMs])

  const handleQueryChange = useCallback(
    (value: string) => {
      store.dispatch({ kind: 'QueryChanged', query: value })
    },
    [store],
  )

  // sidePanel pills-as-search (TASK-412): mirror the host-controlled
  // `query` into the store whenever it changes, so the suggester drives
  // its 6 phases off the active pill's text exactly as the built-in
  // SearchField would in overlay. `QueryChanged` is the same action the
  // SearchField dispatches (same debounce + searchNonce path). A no-op
  // when the value already matches (reducer bails on an identical query).
  useEffect(() => {
    if (!isSidePanel || controlledQuery === undefined) return
    if (store.getState().query === controlledQuery) return
    store.dispatch({ kind: 'QueryChanged', query: controlledQuery })
  }, [isSidePanel, controlledQuery, store])

  // A single selection path — used by every row in every section.
  // TASK-412 Y — emit the user-facing output (which dismisses the picker)
  // FIRST so the row selects + dismisses immediately; the recents persist,
  // analytics event, and §13 selection POST all TRAIL it and never block
  // the tap. The recents write is synchronous local I/O so it can lead
  // safely; the analytics + backend POST are fire-and-forget. Mirrors iOS
  // `_select` (outputs lead, side-effects trail).
  const select = useCallback(
    (station: SuggesterStation, source: SuggesterSelectSource) => {
      // Output first — selects the slot + dismisses the picker.
      onStationSelected(station, slot)
      // Side-effects trail (fire-and-forget — never block the select):
      const nextRecents = pushRecent(store.getState().recentIds, station.id)
      saveRecents(nextRecents)
      store.dispatch({ kind: 'RecentsLoaded', recentIds: nextRecents })
      onAnalytics(EVENT_SELECT, { slot, source, station_id: station.id })
      // §13 — record the pick for Popular ranking, fire-and-forget. Skip
      // off-schema stations (no naptanId). Never blocks the selection.
      if (station.naptanId !== undefined) {
        onRecordSelection?.({ naptanId: station.naptanId, slot })
      }
    },
    [store, onAnalytics, onStationSelected, onRecordSelection, slot],
  )

  // §5 — open Disruption Details from a Closed / Advisory row's status
  // composite. The reducer resolves the inline / cached status to decide
  // rich vs loading; we fire the §A1 analytics event with the resolved
  // severity (the cache fallback mirrors the reducer precedence).
  const openDisruption = useCallback(
    (station: SuggesterStation) => {
      const resolved =
        station.status ??
        (station.naptanId !== undefined
          ? store.getState().cachedStatuses[station.naptanId]
          : undefined)
      store.dispatch({ kind: 'DisruptionOpened', station })
      onAnalytics(EVENT_DISRUPTION_OPEN, {
        station_id: station.id,
        severity: resolved?.severity ?? 'advisory',
      })
    },
    [store, onAnalytics],
  )

  const dismissDisruption = useCallback(() => {
    store.dispatch({ kind: 'DisruptionDismissed' })
  }, [store])

  const retryDisruption = useCallback(() => {
    store.dispatch({ kind: 'DisruptionRetry' })
  }, [store])

  // §5.2 — Loading-shimmer cap. While Details sits in `loading`, schedule
  // the 5 s cap; it drops to `error` unless a late cache write flips it
  // to `rich` first (which clears `loading` and cancels this timer). Keyed
  // off the station id + phase so re-entering loading (Retry) reschedules.
  const disruptionPhase = state.disruption?.phase
  const disruptionStationId = state.disruption?.station.id
  useEffect(() => {
    if (disruptionPhase !== 'loading' || disruptionStationId === undefined) return
    const handle = window.setTimeout(() => {
      store.dispatch({ kind: 'DisruptionLoadingExpired', stationId: disruptionStationId })
    }, DISRUPTION_LOADING_CAP_MS)
    return () => window.clearTimeout(handle)
  }, [disruptionPhase, disruptionStationId, store])

  // G-10 — disruption_detail_loaded / _error on the TERMINAL content / error
  // settle of the Disruption Details screen. The screen settles to `rich`
  // (content, from cache) or `error` (5 s cap → timeout); `loading` is the
  // non-terminal placeholder and must NOT emit. Fire once per terminal settle:
  // the latch ref holds the `station:phase` key we last emitted for, so a
  // recompose at the same terminal phase doesn't re-fire, while a fresh open
  // (different station, or Retry → loading → rich again) does. Mirrors the
  // iOS `.detailLoaded` / `.detailError` reducer emits.
  const detailSettleLatchRef = useRef<string | null>(null)
  useEffect(() => {
    const d = state.disruption
    if (d === null) {
      // Screen dismissed — reset so the next open can emit afresh.
      detailSettleLatchRef.current = null
      return
    }
    if (d.phase === 'loading') return // non-terminal placeholder, no emit
    const key = `${d.station.id}:${d.phase}`
    if (detailSettleLatchRef.current === key) return
    detailSettleLatchRef.current = key
    // Pure mapping (store.disruptionSettleEvent) → loaded / error wire shape.
    const settle = disruptionSettleEvent(d)
    if (settle !== null) onAnalytics(settle.name, settle.params)
  }, [state.disruption, onAnalytics])

  // §8 — push the host's live-status snapshot into the store (WHOLESALE
  // REPLACE, TASK-629 — see `CachedStatusesUpdated` in store.ts), then
  // project it onto rows. The effect runs whenever the host pushes a new
  // snapshot (e.g. the 60 s refresh tick); `CachedStatusesUpdated` is a
  // no-op when the incoming snapshot is identical to what's cached.
  useEffect(() => {
    if (cachedStatuses === undefined) return
    const statuses: Record<string, NonNullable<SuggesterStation['status']>> = {}
    for (const [naptanId, status] of Object.entries(cachedStatuses)) {
      if (status !== undefined) statuses[naptanId] = status
    }
    store.dispatch({ kind: 'CachedStatusesUpdated', statuses })
  }, [cachedStatuses, store])

  const sections = useMemo(() => {
    const built = buildSections({
      status: state.status,
      results: state.results,
      nearby: state.status === 'nearby' ? state.nearby : state.nearbyIdle,
      recentIds: state.recentIds,
      favoriteIds: state.favoriteIds,
      catalogue,
      popular,
    })
    // §8 late-arrival reactivity — project the merged status cache onto
    // every rendered row so a status-less payload still shows its badge
    // once `/status` lands a disruption for the same naptanId.
    return built.map((section) => {
      const projected = applyCachedStatuses(section.stations, state.cachedStatuses)
      return projected === section.stations ? section : { ...section, stations: projected }
    })
  }, [
    state.status,
    state.results,
    state.nearby,
    state.nearbyIdle,
    state.recentIds,
    state.favoriteIds,
    state.cachedStatuses,
    catalogue,
    popular,
  ])

  // Apply a nearby resolution outcome to the store: granted+fix → Nearby;
  // granted but no fix → no-op; denied → the denied alert. `atMount`
  // distinguishes the granted-at-mount probe (→ IDLE composition, Nearby
  // + Popular together) from an explicit banner tap (→ full-screen
  // `nearby` status). Mirrors the iOS `locationResolvedAtMount` split.
  const applyResolution = useCallback(
    (resolution: NearbyResolution, atMount: boolean) => {
      store.dispatch({ kind: 'LocationPermissionChanged', permission: resolution.permission })
      if (resolution.permission === 'denied') {
        store.dispatch({ kind: 'AlertRequested', alert: 'denied' })
        return
      }
      if (resolution.stations !== undefined && resolution.stations.length > 0) {
        store.dispatch({
          kind: atMount ? 'NearbyResolvedAtMount' : 'NearbyResolved',
          stations: resolution.stations,
        })
      }
    },
    [store],
  )

  const runResolve = useCallback(
    (atMount = false) => {
      setResolvingLocation(true)
      resolveNearby()
        .then((resolution) => applyResolution(resolution, atMount))
        .catch(() => {
          // Resolution failed unexpectedly — fall back to the denied alert
          // so the user has a recovery path.
          store.dispatch({ kind: 'AlertRequested', alert: 'denied' })
        })
        .finally(() => setResolvingLocation(false))
    },
    [resolveNearby, applyResolution, store],
  )

  // #38 (spec §14) — mount-time permission probe. Reads the browser's
  // current geolocation permission WITHOUT prompting; the reducer owns
  // the banner visibility from the result. `granted` → the reducer hides
  // the banner (LocationProbed) and we auto-resolve Nearby so the user
  // lands directly on the Nearby section. `prompt` / `denied` → the
  // banner stays and we take no action (the user still has the tap). The
  // StrictMode-safe latch guards the dev double-invoke. Mirrors the iOS
  // `.task` probe + the `NearbyResolved` defence flip.
  const probeReportedRef = useRef(false)
  useEffect(() => {
    if (probePermission === undefined || probeReportedRef.current) return
    probeReportedRef.current = true
    let cancelled = false
    void probePermission().then((permission) => {
      if (cancelled) return
      store.dispatch({ kind: 'LocationProbed', permission })
      // Granted-at-mount: resolve into the IDLE composition (Nearby +
      // Popular together), NOT the full-screen `nearby` status. The
      // full-screen Nearby phase is reserved for an explicit banner tap.
      if (permission === 'granted') runResolve(true)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Location banner tap — branch on the current permission (spec §14):
  // `prompt` / `granted` → run the host resolution directly. The
  // host's `resolveNearby` calls `navigator.geolocation`, so on
  // `prompt` the BROWSER shows its own native permission prompt — the
  // Suggester renders NO in-app UI for that step. `denied` → the
  // app-drawn "Location Access Required" alert (the OS will not
  // re-prompt, so an app affordance is the only recovery path).
  const handleUseLocation = useCallback(() => {
    if (resolvingLocation) return
    if (store.getState().locationPermission === 'denied') {
      store.dispatch({ kind: 'AlertRequested', alert: 'denied' })
      return
    }
    runResolve()
  }, [resolvingLocation, store, runResolve])

  const dismissAlert = useCallback(() => {
    store.dispatch({ kind: 'AlertDismissed' })
  }, [store])

  const handleOpenSettings = useCallback(() => {
    store.dispatch({ kind: 'AlertDismissed' })
    onOpenSettings?.()
  }, [store, onOpenSettings])

  const sectionTitleFor = (key: SuggesterSectionKey): string => {
    switch (key) {
      case 'nearby':
        return strings.nearby
      case 'favorites':
        return strings.favorites
      case 'recents':
        return strings.recents
      case 'popular':
        return strings.popular
      case 'results':
        // The `results` section is rendered headless (no header) — see
        // the `headless` prop below. Title is unused; return empty.
        return ''
    }
  }

  // `sidePanel` swaps the full-screen overlay chrome for the floating
  // card chrome. `aria-modal` stays only for the full-screen overlay —
  // the side panel is an inline region of the expanded sidebar, not a
  // viewport-blocking modal. In sidePanel the header (SearchField +
  // close) is OMITTED entirely (TASK-412 pills-as-search): the host's
  // RouteCard active pill is the query input and the only chrome.
  return (
    <div
      className={isSidePanel ? styles.sidePanel : styles.overlay}
      role="dialog"
      aria-modal={isSidePanel ? undefined : true}
      data-testid="suggester"
    >
      {!isSidePanel && (
        <div className={styles.header}>
          <SearchField
            value={state.query}
            placeholder={strings.searchPlaceholder}
            clearLabel={strings.clearSearch}
            onChange={handleQueryChange}
          />
          <button
            type="button"
            className={styles.close}
            data-testid="suggester-close"
            aria-label={strings.close}
            onClick={onClose}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              aria-hidden
              focusable="false"
              fill="none"
            >
              <path
                d="M4.5 4.5 L12.5 12.5 M12.5 4.5 L4.5 12.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Location banner — visibility is REDUCER-OWNED (#38, spec §14):
          shown only while `showsLocationBanner` is true. The reducer
          clears it on a `granted` permission probe at mount and on a
          resolved Nearby fix, so a granted-from-start user never sees it
          and the Nearby frame (post-resolve) has no banner — matching
          the Figma Nearby frame. Host code always assumes it starts
          visible and lets the reducer correct it. */}
      {state.showsLocationBanner && (
        <div className={styles.bannerSlot}>
          <LocationBanner
            title={strings.locationBannerTitle}
            ctaLabel={strings.locationBannerCta}
            resolving={resolvingLocation}
            onUseLocation={handleUseLocation}
          />
        </div>
      )}

      {state.status === 'offline' && (
        <div className={styles.noticeSlot}>
          <OfflineNotice text={strings.offlineNotice} />
        </div>
      )}

      <div
        className={`${styles.body} ${state.status === 'empty' ? styles.bodyCentred : ''}`.trim()}
      >
        {state.status === 'loading' && (
          <div className={styles.skeletonList} data-testid="suggester-loading">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        )}

        {state.status === 'empty' && (
          <EmptyState title={strings.emptyTitle} subtitle={strings.emptySubtitle} />
        )}

        {sections.map((section) => {
          // Section drives the analytics `source`: a Recents-row tap is
          // `recent`, a Nearby-row tap is `location`, everything else is
          // `row`.
          const source: SuggesterSelectSource =
            section.key === 'recents' ? 'recent' : section.key === 'nearby' ? 'location' : 'row'
          // The `results` section (used by both `results` and `offline`)
          // has NO section header in the Figma design (node 6177:15851)
          // — it is a bare result list. Every other section keeps its
          // icon + label header.
          const headless = section.key === 'results'
          return (
            <SuggestionSection
              key={section.key}
              sectionKey={section.key}
              title={sectionTitleFor(section.key)}
              stations={section.stations}
              onSelect={(station) => select(station, source)}
              onDisruptionTap={openDisruption}
              headless={headless}
              closedLabel={strings.stationClosed}
              advisoryLabel={strings.serviceAlert}
            />
          )
        })}
      </div>

      {/* "Location Access Required" — the ONLY in-app location alert.
          App-drawn because the OS will not re-prompt once permission is
          denied, so the app must offer a recovery path. The system
          permission *prompt* is NOT rendered here — it is the browser's
          own native dialog, shown by `navigator.geolocation` inside the
          host `resolveNearby`. */}
      {state.alert === 'denied' && (
        <SuggesterAlert
          title={strings.deniedAlertTitle}
          body={strings.deniedAlertBody}
          layout="row"
          actions={[
            { label: strings.deniedCancel, onPress: dismissAlert },
            {
              label: strings.deniedOpenSettings,
              onPress: handleOpenSettings,
              emphasised: true,
            },
          ]}
          onBackdrop={dismissAlert}
          dismissLabel={strings.dismiss}
        />
      )}

      {/* Disruption Details (spec §5) — pushed over the picker (the web
          equivalent of the iOS NavigationStack push). Covers the picker
          via `position: absolute; inset: 0`; Back / Escape pops it. */}
      {state.disruption !== null && (
        <DisruptionDetails
          presentation={state.disruption}
          lines={lines ?? {}}
          onBack={dismissDisruption}
          onRetry={retryDisruption}
          strings={strings}
          // O3: in the side panel DD renders transparently over the host
          // glass (in-panel swap, no opaque plate). Explicit prop — not
          // context — so a portal boundary can't drop it.
          transparent={isSidePanel}
        />
      )}
    </div>
  )
}
